import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_DATA_DASHBOARD } from "../config/queries";

const EVENTS_CACHE_TIME_SECONDS = 30;

export default function useMultipleEvents(addresses = []) {
  const [events, setEvents] = useState<any[]>([]);
  const prevAddressesRef = useRef<string[]>([]);
  
  const stableAddresses = useMemo(() => {
    const prevAddresses = prevAddressesRef.current;
    const addressesStr = JSON.stringify(addresses);
    const prevAddressesStr = JSON.stringify(prevAddresses);
    
    if (addressesStr === prevAddressesStr) {
      return prevAddresses;
    }
    
    prevAddressesRef.current = addresses;
    return addresses;
  }, [addresses]);
  
  const [_getDataDashboard] = useLazyQuery(GET_DATA_DASHBOARD, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    context: {
      fetchOptions: {
        cache: "force-cache",
        cacheTime: EVENTS_CACHE_TIME_SECONDS * 1000, // Convert to milliseconds
      },
    },
  });

  const refresh = useCallback(async () => {
    if (!stableAddresses?.length) {
      setEvents([]);
      return;
    }
    
    try {
      const results: any = await Promise.allSettled(
        stableAddresses.map((_address) =>
          _getDataDashboard({ variables: { address: _address } }),
        ),
      );
      
      const newEvents = results
        .map(
          (e, index) =>
            e?.value?.data?.getDataDashboard?.map((ev: any) => ({
              ...ev,
              sender: stableAddresses?.[index],
            })) || [],
        )
        .flat();
      
      setEvents(prevEvents => {
        if (JSON.stringify(prevEvents) === JSON.stringify(newEvents)) {
          return prevEvents;
        }
        return newEvents;
      });
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  }, [stableAddresses, _getDataDashboard]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return events;
}
